import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../utils/supabaseClient';

const OrganizationChart = ({ departments, onDepartmentSelect, selectedDepartment }) => {
  const [viewMode, setViewMode] = useState('chart'); // 'chart', 'list', or 'profile'
  const [orgStaff, setOrgStaff] = useState([]);
  const [preview, setPreview] = useState({ open: false, src: '', alt: '' });

  // Load staff data from database
  const loadOrgStaff = async () => {
    try {
      const { data, error } = await supabase
        .from('org_structure')
        .select('*')
        .order('id');
      
      if (error) {
        console.error('Error loading org staff:', error);
        return;
      }
      
      console.log('Loaded org staff:', data);
      console.log('Staff with images:', data?.filter(staff => staff.image_url && staff.image_url !== 'null' && staff.image_url !== ''));
      setOrgStaff(data || []);
    } catch (error) {
      console.error('Error loading org staff:', error);
    }
  };

  useEffect(() => {
    loadOrgStaff();
  }, []);

  // Auto-refresh when switching view tabs or departments list updates
  useEffect(() => {
    loadOrgStaff();
  }, [viewMode, departments]);

  // Listen to admin updates and refresh
  useEffect(() => {
    const handler = () => loadOrgStaff();
    window.addEventListener('org-data-updated', handler);
    return () => window.removeEventListener('org-data-updated', handler);
  }, []);

  // Supabase Realtime: auto-refresh org_structure changes across devices
  useEffect(() => {
    try {
      const channel = supabase
        .channel('realtime-org-structure')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'org_structure' },
          () => {
            loadOrgStaff();
          }
        )
        .subscribe();

      return () => {
        try { supabase.removeChannel(channel); } catch {}
      };
    } catch (e) {
      console.warn('Realtime not available:', e);
    }
  }, []);

  // Helper function to validate and get image URL
  const getValidImageUrl = (imageUrl, fallbackUrl = '/assets/images/no_image.png') => {
    if (!imageUrl || imageUrl === 'null' || imageUrl === '' || imageUrl === 'undefined') {
      return fallbackUrl;
    }
    return imageUrl;
  };

  // Allow dropping staff images into a public folder and matching by file name
  // Usage: place files in /public/assets/org-staff with file name = person's name
  // Supported variations: "ชื่อ-นามสกุล", "ชื่อ นามสกุล", "ชื่อนามสกุล" and extensions jpg/jpeg/png/webp
  const LOCAL_STAFF_IMAGE_DIR = '/assets/org-staff';
  const buildLocalImageCandidates = (personName) => {
    const exts = ['jpg', 'jpeg', 'png', 'webp'];
    const raw = (personName || '').toString().trim();
    if (!raw) return [];
    const variants = new Set();
    variants.add(raw); // exact
    variants.add(raw.replace(/\s+/g, ' ')); // normalized spaces
    variants.add(raw.replace(/\s+/g, '-')); // dashes
    variants.add(raw.replace(/\s+/g, '_')); // underscores
    variants.add(raw.replace(/\s+/g, '')); // no spaces
    // Build full paths
    const paths = [];
    variants.forEach((v) => exts.forEach((ext) => paths.push(`${LOCAL_STAFF_IMAGE_DIR}/${v}.${ext}`)));
    return paths;
  };

  // Image component that tries multiple candidate URLs (local-first) then falls back to initials via parent
  const FallbackImage = ({ name, dbUrl, sizeClass = 'w-20 h-20', className = '' }) => {
    const candidates = React.useMemo(() => {
      // Smart priority:
      // - If dbUrl is a real uploaded image (not placeholder), use it first (admin override)
      // - Otherwise, try local files by name in public/assets/org-staff
      const local = buildLocalImageCandidates(name);
      const isPlaceholder = !dbUrl || /\/assets\/images\/no_image\.png$/i.test(dbUrl) || dbUrl === 'null' || dbUrl === 'undefined';
      const list = [];
      if (!isPlaceholder) list.push(dbUrl);
      list.push(...local);
      // If nothing found locally and we only had placeholder blocked, allow it as the last resort
      if (isPlaceholder && dbUrl) list.push(dbUrl);
      return list;
    }, [name, dbUrl]);
    const [idx, setIdx] = React.useState(0);
    const src = candidates[idx];
    if (!src) return null;
    return (
      <img
        src={src}
        alt={name}
        className={`${sizeClass} ${className}`}
        onError={() => setIdx((i) => i + 1)}
        onClick={() => setPreview({ open: true, src, alt: name || '' })}
        style={{ cursor: 'zoom-in' }}
      />
    );
  };

  // Robust matcher: map department head -> staff record from org_structure (avoid loose partial matches)
  const findStaffForDepartment = (dept) => {
    if (!dept) return null;
    const normalize = (s) => (s || '').toString().toLowerCase().replace(/\s+/g, '').trim();

    // 1) Exact name match (strict)
    const byExactName = orgStaff.find((s) => s?.name && dept?.head && s.name.trim() === dept.head.trim());
    if (byExactName) return byExactName;

    // 2) Exact position + exact department (both must match)
    const byExactPosDept = orgStaff.find((s) =>
      s?.position && dept?.headPosition && s.position.trim() === dept.headPosition.trim() &&
      s?.department && dept?.name && s.department.trim() === dept.name.trim()
    );
    if (byExactPosDept) return byExactPosDept;

    // 3) Normalized name equality (case/space-insensitive)
    const byNormalizedName = orgStaff.find((s) => normalize(s?.name) === normalize(dept?.head));
    if (byNormalizedName) return byNormalizedName;

    // 4) Exact position only (last resort; may not be unique but better than partial includes)
    const byExactPosition = orgStaff.find((s) => s?.position && dept?.headPosition && s.position.trim() === dept.headPosition.trim());
    if (byExactPosition) return byExactPosition;

    // Do NOT use loose substring matching to prevent wrong photos
    return null;
  };

  // Staff data with profile images and positions (fallback data)
  const staffProfiles = {
    // Level 1 - Top Management
    1: {
      name: 'นายเอนก ปันทะยม',
      position: 'นายอำเภอเทิง',
      department: 'ที่ทำการปกครองอำเภอเทิง',
      avatar: '/assets/images/no_image.png',
      phone: '053-795-345',
      email: 'info@thoeng.go.th'
    },
    // Level 2 - Department Heads
    2: {
      name: 'นายกฤษฎา ชัยยา',
      position: 'หัวหน้ากลุ่มงานบรหารงานปกครอง',
      department: 'ฝ่ายบริหารงานปกครอง',
      avatar: '/assets/images/no_image.png',
      phone: '053-795-345',
      email: 'info@thoeng.go.th'
    },
    // Level 3 - Group Leaders
    3: {
      name: 'นายบรรพต โพธิ์มา',
      position: 'หัวหน้ากลุ่มงานทะเบียนและบัตร',
      department: 'กลุ่มงานทะเบียนและบัตร',
      avatar: '/assets/images/no_image.png',
      phone: '053-795-345',
      email: 'info@thoeng.go.th',
      subordinatesByLevel: [
        // ชั้นที่ 2
        [
          {
            name: 'ส.ต.อ.สาคร ช่วยหนูจันทร์',
            position: 'ปลัดอำเภอ(จพง.ปค.ชำนาญการ)',
            avatar: '/assets/images/no_image.png',
          },
          {
            name: 'นางสาวบุณยวีร์ แซ่ย่าง',
            position: 'ปลัดอำเภอ(จพง.ปค.ปฏิบัติการ)',
            avatar: '/assets/images/no_image.png',
          }
        ],
        // ชั้นที่ 3
        [
          {
            name: 'นายเกรียงไกร ตระการไทย',
            position: 'เจ้าหน้าที่ปกครองชำนาญงาน',
            avatar: '/assets/images/no_image.png',
          }
        ],
        // ชั้นที่ 4
        [
          { name: 'นางมณินทร เรืองสิริสถาน', position: 'ลูกจ้าง ส.ย.', avatar: '/assets/images/no_image.png' },
          { name: 'นางชมัยพร รวงทอง', position: 'ลูกจ้าง ส.ย.', avatar: '/assets/images/no_image.png' },
          { name: 'นางสาววรรณศิริ พุ่มทวี', position: 'ลูกจ้าง ส.ย.', avatar: '/assets/images/no_image.png' },
          { name: 'นายอรรถวิชญ์ ตันต้าว', position: 'อ.ส.ช่วยงานทะเบียนและบัตร', avatar: '/assets/images/no_image.png' }
        ]
      ]
    },
    4: {
      name: 'นายกฤตชัย สุวรรณ์',
      position: 'ปลัดอำเภอ(จพง.ปค.ชำนาญการ)',
      department: 'กลุ่มงานความมั่นคง',
      avatar: '/assets/images/no_image.png',
      phone: '053-795-345',
      email: 'info@thoeng.go.th',
      subordinatesByLevel: [
        // ชั้นที่ 2
        [
          { name: 'นายสราวุธ ใหม่เทวินทร์', position: 'ปลัดอำเภอ(จพง.ปค.ปฏิบัติการ)', avatar: '/assets/images/no_image.png' }
        ],
        // ชั้นที่ 3
        [
          { name: 'นายสุรชัย ต๊ะมา', position: 'พนักงานราชการ', avatar: '/assets/images/no_image.png' },
          { name: 'นางนาตยา พรมงาม', position: 'ลูกจ้าง ส.ย.', avatar: '/assets/images/no_image.png' }
        ],
        // ชั้นที่ 4
        [
          { name: 'นายชัยวัฒน์ ณรงค์พันธ์ชัย', position: 'อ.ส.ช่วยงานความมั่นคง', avatar: '/assets/images/no_image.png' }
        ],
        // ชั้นที่ 5
        [
          { name: 'นางสาวกรรณิกา ศรีวิชัย', position: 'ลูกจ้างยาเสพติด', avatar: '/assets/images/no_image.png' }
        ]
      ]
    },
    5: {
      name: 'นางรำพรรณ หายหัตถี',
      position: 'จพง.การเงินและบัญชี',
      department: 'กลุ่มงานการเงินและบัญชี',
      avatar: '/assets/images/no_image.png',
      phone: '053-795-345',
      email: 'info@thoeng.go.th',
      subordinatesByLevel: [
        // ชั้นที่ 2
        [
          { name: 'นางชมนพร อาจนนลา', position: 'อ.ส.ช่วยงานเสมียนตรา', avatar: '/assets/images/no_image.png' }
        ],
        // ชั้นที่ 3
        [
          { name: 'นายนิวัฒน์ จันนวล', position: 'พนักงานสถานที่', avatar: '/assets/images/no_image.png' }
        ]
      ]
    },
    6: {
      name: 'นางลัดดา สมหนองบัว',
      position: 'จพง.ปค.ชำนาญการ',
      department: 'สำนักงานอำเภอ',
      avatar: '/assets/images/no_image.png',
      phone: '053-795-345',
      email: 'info@thoeng.go.th',
      subordinatesByLevel: [
        // ชั้นที่ 2
        [
          { name: 'นางสาวจารุวรรณ ศรีอำคา', position: 'พนักงานราชการ', avatar: '/assets/images/no_image.png' },
          { name: 'นางสาววัชรินทร์ การินทา', position: 'ลูกจ้าง ส.ย.', avatar: '/assets/images/no_image.png' },
          { name: 'นางสาวศิริขวัญ อารีย์', position: 'ลูกจ้างกาชาด', avatar: '/assets/images/no_image.png' }
        ]
      ]
    },
    7: {
      name: 'นายพัฒณพงษ์ นาคะพงษ์',
      position: 'จพง.ปค.ชำนาญการ',
      department: 'กลุ่มงานบริหารงานปกครอง',
      avatar: '/assets/images/no_image.png',
      phone: '053-795-345',
      email: 'info@thoeng.go.th',
      subordinatesByLevel: [
        // ชั้นที่ 2
        [
          { name: 'นางพรนภา สุวรรณขัดศรี', position: 'เจ้าหน้าที่ปกครองชำนาญงาน', avatar: '/assets/images/no_image.png' }
        ],
        // ชั้นที่ 3
        [
          { name: 'นางสาวขนิษฐา พรมมา', position: 'ลูกจ้าง ส.ย.', avatar: '/assets/images/no_image.png' },
          { name: 'นางสาวหนึ่ง ไชยชนะ', position: 'ลูกจ้างกาชาด', avatar: '/assets/images/no_image.png' }
        ]
      ]
    },
    'dept-8': {
      name: 'นางสาวกฤษณา สมวุฒิ',
      position: 'จพง.ปค.ปฏิบัติการ',
      department: 'กลุ่มงานอำนวยความเป็นธรรม',
      avatar: '/assets/images/no_image.png',
      phone: '053-795-345',
      email: 'info@thoeng.go.th'
    }
  };

  // Build helpers for computing staff counts from children
  const departmentById = React.useMemo(() => {
    const map = new Map();
    departments?.forEach((d) => map.set(d?.id, d));
    return map;
  }, [departments]);

  const childrenByParent = React.useMemo(() => {
    const map = new Map();
    departments?.forEach((d) => {
      const key = d?.parentId ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    });
    return map;
  }, [departments]);

  const computedCountMemo = React.useRef(new Map());
  const computeStaffCount = (deptId) => {
    const memo = computedCountMemo.current;
    if (memo.has(deptId)) return memo.get(deptId);
    const dept = departmentById.get(deptId);
    if (!dept) return 0;
    // Leaf groups (level 3) use their own staffCount
    if (dept?.level === 3) {
      memo.set(deptId, Number(dept?.staffCount || 0));
      return memo.get(deptId);
    }
    // Sum children counts; fallback to dept.staffCount if no children
    const children = childrenByParent.get(deptId) || [];
    const sum = children.length > 0
      ? children.reduce((acc, child) => acc + computeStaffCount(child.id), 0)
      : Number(dept?.staffCount || 0);
    memo.set(deptId, sum);
    return sum;
  };

  const hierarchyLevels = [
    {
      level: 1,
      title: 'ผู้บริหารระดับสูง',
      departments: departments?.filter(dept => dept?.level === 1)
    },
    {
      level: 2,
      title: 'หัวหน้าฝ่าย',
      departments: departments?.filter(dept => dept?.level === 2)
    },
    {
      level: 3,
      title: 'หัวหน้ากลุ่มงาน',
      departments: departments?.filter(dept => dept?.level === 3)
    }
  ];

  // --- Auto-fit chart view ---
  // 1. ลบ useRef/useEffect/scale ที่เกี่ยวกับ auto-fit chart view
  // 2. ตรวจสอบและแก้ไข profile view ให้แสดงครบทุกกลุ่ม/ทุกคน
  const renderChartView = () => (
    <div className="w-full overflow-x-auto">
      <div className="space-y-8 min-w-fit mx-auto">
      {hierarchyLevels?.map((level) => (
        <div key={level?.level} className="text-center">
          <h3 className="text-lg font-semibold text-primary mb-6">
            {level?.title}
          </h3>
          <div className={`flex ${level?.level === 3 ? 'flex-nowrap overflow-x-auto justify-center gap-4 px-1' : 'flex-wrap justify-center gap-6'}`}>
            {level?.departments?.map((dept) => (
              <div key={dept?.id} className="relative">
                <button
                  onClick={() => !dept?.isEmpty && onDepartmentSelect(dept)}
                  disabled={dept?.isEmpty}
                  className={`
                    bg-card border-2 rounded-lg p-4 ${level?.level === 3 ? 'w-56 shrink-0' : 'w-64'} text-left
                    transition-government hover:shadow-government-lg
                    ${dept?.isEmpty ? 'opacity-50 cursor-not-allowed' : ''}
                    ${selectedDepartment?.id === dept?.id 
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }
                  `}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`
                      ${level?.level === 3 ? 'w-10 h-10' : 'w-12 h-12'} rounded-full flex items-center justify-center
                      ${dept?.level === 1 ? 'bg-primary text-primary-foreground' :
                        dept?.level === 2 ? 'bg-secondary text-secondary-foreground': 'bg-accent text-accent-foreground'}
                    `}>
                      <Icon name={dept?.icon} size={level?.level === 3 ? 16 : 20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      {dept?.isEmpty ? (
                        <div className="text-center py-2">
                          <div className={`text-muted-foreground ${level?.level === 3 ? 'text-xs' : 'text-sm'}`}>
                            หน่วยงานว่าง
                          </div>
                        </div>
                      ) : (
                        <>
                          <h4 className={`font-semibold text-foreground ${level?.level === 3 ? 'text-xs' : 'text-sm'} mb-1`}>
                            {dept?.name}
                          </h4>
                          <p className={`${level?.level === 3 ? 'text-xs' : 'text-xs'} text-muted-foreground mb-2`}>
                            {dept?.head}
                          </p>
                          <div className={`flex items-center ${level?.level === 3 ? 'text-xs' : 'text-xs'} text-muted-foreground`}>
                            <Icon name="Users" size={10} className="mr-1" />
                            {computeStaffCount(dept?.id)} คน
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </button>
                
                {/* Connection lines for chart view */}
                {level?.level < 3 && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                    <div className="w-px h-6 bg-border"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Horizontal connection line */}
          {level?.level < 3 && level?.departments?.length > 1 && (
            <div className="flex justify-center mt-6">
              <div className="h-px bg-border" style={{ width: `${level?.departments?.length * 280}px` }}></div>
            </div>
          )}
        </div>
      ))}
      </div>
    </div>
  );

  const renderListView = () => (
    <div className="space-y-6">
      {hierarchyLevels?.map((level) => (
        <div key={level?.level}>
          <h3 className="text-lg font-semibold text-primary mb-4 border-b border-border pb-2">
            {level?.title}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {level?.departments?.map((dept) => (
              <button
                key={dept?.id}
                onClick={() => !dept?.isEmpty && onDepartmentSelect(dept)}
                disabled={dept?.isEmpty}
                className={`
                  bg-card border rounded-lg p-4 text-left
                  transition-government hover:shadow-government
                  ${dept?.isEmpty ? 'opacity-50 cursor-not-allowed' : ''}
                  ${selectedDepartment?.id === dept?.id 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }
                `}
              >
                <div className="flex items-start space-x-3">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${dept?.level === 1 ? 'bg-primary text-primary-foreground' :
                      dept?.level === 2 ? 'bg-secondary text-secondary-foreground': 'bg-accent text-accent-foreground'}
                  `}>
                    <Icon name={dept?.icon} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {dept?.isEmpty ? (
                      <div className="text-center py-2">
                        <div className="text-muted-foreground text-sm">
                          หน่วยงานว่าง
                        </div>
                      </div>
                    ) : (
                      <>
                        <h4 className="font-semibold text-foreground text-sm mb-1">
                          {dept?.name}
                        </h4>
                        <p className="text-xs text-muted-foreground mb-2">
                          {dept?.head}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span className="flex items-center">
                            <Icon name="Users" size={12} className="mr-1" />
                            {computeStaffCount(dept?.id)} คน
                          </span>
                          <Icon name="ChevronRight" size={12} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // ฟังก์ชันแสดงแผนผังย่อยแนวตั้งสำหรับกลุ่มงานทะเบียนและบัตร
  const renderSubTreeProfile = (profile) => {
    console.log('Rendering sub tree profile:', profile.name, 'avatar:', profile.avatar);
    
    return (
    <div className="flex flex-col items-center gap-4">
      {/* หัวหน้ากลุ่มงาน */}
      <div className="bg-white rounded-2xl p-4 shadow-md border border-gray-200 w-56">
        <div className="text-center">
          <div className="relative mx-auto mb-3">
          <div className="relative w-14 h-14 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold">
                {profile?.name?.charAt(0) || '?'}
              </div>
              <div className="absolute inset-0">
                <FallbackImage name={profile?.name} dbUrl={profile?.avatar} sizeClass="w-14 h-14 rounded-full object-cover" />
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
              <Icon name="Check" size={10} className="text-white" />
            </div>
          </div>
          <h4 className="text-base font-bold text-gray-900 mb-1">{profile?.name}</h4>
          <p className="text-xs font-semibold text-blue-600 mb-1">{profile?.position}</p>
          <p className="text-xs text-gray-600 mb-2 leading-relaxed">{profile?.department}</p>
        </div>
      </div>
      {/* subordinatesByLevel: render เป็นแถวตามลำดับชั้น */}
      {profile?.subordinatesByLevel?.map((levelArr, levelIdx) => (
        <div key={levelIdx} className="flex flex-row justify-center gap-4">
            {levelArr.map((sub, idx) => {
              // หาข้อมูลลูกน้องจากฐานข้อมูล
              const dbSubordinate = orgStaff.find(staff => {
                // Exact name match (highest priority)
                if (staff.name && sub.name && staff.name.trim() === sub.name.trim()) {
                  return true;
                }
                // Partial name match (contains)
                if (staff.name && sub.name && 
                    (staff.name.includes(sub.name) || sub.name.includes(staff.name))) {
                  return true;
                }
                return false;
              });
              
              // ใช้รูปภาพจากฐานข้อมูลถ้ามี (ให้ FallbackImage จัดการหาไฟล์จากโฟลเดอร์ด้วยชื่อบุคคล)
              const finalAvatar = dbSubordinate?.image_url || '';
              
              console.log('Rendering subordinate:', sub.name, 'dbStaff:', dbSubordinate?.name, 'finalAvatar:', finalAvatar);
              
              return (
            <div key={sub.name + idx} className="bg-white rounded-2xl p-4 shadow border border-gray-200 w-56">
              <div className="text-center">
                <div className="relative w-12 h-12 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-blue-200 to-blue-400 flex items-center justify-center shadow mb-2">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-bold">
                    {sub.name?.charAt(0) || '?'}
                  </div>
                  <div className="absolute inset-0">
                    <FallbackImage name={sub.name} dbUrl={finalAvatar} sizeClass="w-12 h-12 rounded-full object-cover" />
                  </div>
                </div>
                <h5 className="font-semibold text-gray-900 text-sm">{sub.name}</h5>
                <p className="text-xs text-blue-600">{sub.position}</p>
              </div>
            </div>
              );
            })}
        </div>
      ))}
    </div>
  );
  };

  // 2. ตรวจสอบและแก้ไข profile view ให้แสดงครบทุกกลุ่ม/ทุกคน
  const renderProfileView = () => (
    <div className="space-y-8">
      {/* Level 1: นายอำเภอ */}
      <div className="flex justify-center">
        {hierarchyLevels[0]?.departments?.map((dept) => {
          // Strict staff lookup to avoid wrong image
          const dbStaff = findStaffForDepartment(dept);
          
          console.log('Department:', dept?.name, 'Head:', dept?.head, 'Found staff:', dbStaff);
          console.log('dbStaff name:', dbStaff?.name, 'dept head:', dept?.head);
           console.log('dbStaff image_url:', dbStaff?.image_url);
           console.log('Available staff:', orgStaff.map(s => ({ name: s.name, position: s.position, image_url: s.image_url })));
          
           console.log('Department:', dept?.name, 'Head:', dept?.head, 'Found staff:', dbStaff);
           console.log('dbStaff name:', dbStaff?.name, 'dept head:', dept?.head);
           console.log('dbStaff image_url:', dbStaff?.image_url);
          
          const baseProfile = staffProfiles[dept?.id] || (dept?.id === 8 ? staffProfiles['dept-8'] : {}) || {};
          const profile = {
            ...baseProfile,
            // ใช้ข้อมูลจาก departments ก่อน (ชื่อที่แก้ไขล่าสุด) ถ้าไม่มีค่อยใช้จาก org_structure
            name: dept?.head || dbStaff?.name || baseProfile?.name,
            position: dept?.headPosition || dbStaff?.position || baseProfile?.position,
            department: dept?.name || baseProfile?.department,
             avatar: getValidImageUrl(dbStaff?.image_url, baseProfile?.avatar),
            phone: dbStaff?.phone || baseProfile?.phone,
            email: dbStaff?.email || baseProfile?.email,
          };
          
          console.log('Final profile name:', profile.name, 'from dbStaff:', dbStaff?.name, 'from dept:', dept?.head);
           console.log('Final profile avatar:', profile.avatar, 'from dbStaff image_url:', dbStaff?.image_url);
          return (
            <div key={dept?.id} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 mx-2 w-72">
              <div className="text-center">
                <div className="relative mx-auto mb-4">
                  <div className="relative w-20 h-20 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-bold">
                      {profile?.name?.charAt(0) || '?'}
                    </div>
                    <div className="absolute inset-0">
                      <FallbackImage name={profile?.name} dbUrl={profile?.avatar} sizeClass="w-20 h-20 rounded-full object-cover" />
                    </div>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-white" />
                  </div>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">{profile?.name}</h4>
                <p className="text-sm font-semibold text-blue-600 mb-2">{profile?.position || 'หัวหน้า'}</p>
                <p className="text-xs text-gray-600 mb-4 leading-relaxed">{profile?.department}</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Phone" size={12} />
                    <span>{profile?.phone || '053-795-345'}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Mail" size={12} />
                    <span className="truncate">{profile?.email || 'info@thoeng.go.th'}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <Icon name="Users" size={14} />
                    <span>{computeStaffCount(dept?.id)} คน</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Level 2: ปลัดอำเภอ */}
      <div className="flex justify-center">
        {hierarchyLevels[1]?.departments?.map((dept) => {
           // Strict staff lookup to avoid wrong image
           const dbStaff = findStaffForDepartment(dept);
          
           console.log('Department:', dept?.name, 'Head:', dept?.head, 'Found staff:', dbStaff);
           console.log('dbStaff name:', dbStaff?.name, 'dept head:', dept?.head);
           console.log('dbStaff image_url:', dbStaff?.image_url);
          
          const baseProfile = staffProfiles[dept?.id] || (dept?.id === 8 ? staffProfiles['dept-8'] : {}) || {};
          const profile = {
            ...baseProfile,
            // ใช้ข้อมูลจาก departments ก่อน (ชื่อที่แก้ไขล่าสุด) ถ้าไม่มีค่อยใช้จาก org_structure
            name: dept?.head || dbStaff?.name || baseProfile?.name,
            position: dept?.headPosition || dbStaff?.position || baseProfile?.position,
            department: dept?.name || baseProfile?.department,
             avatar: getValidImageUrl(dbStaff?.image_url, baseProfile?.avatar),
            phone: dbStaff?.phone || baseProfile?.phone,
            email: dbStaff?.email || baseProfile?.email,
          };
          
          console.log('Final profile:', profile);
           console.log('Final profile avatar:', profile.avatar, 'from dbStaff image_url:', dbStaff?.image_url);
          return (
            <div key={dept?.id} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 mx-2 w-72">
              <div className="text-center">
                <div className="relative mx-auto mb-4">
                  <div className="relative w-20 h-20 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-bold">
                      {profile?.name?.charAt(0) || '?'}
                    </div>
                    <div className="absolute inset-0">
                      <FallbackImage name={profile?.name} dbUrl={profile?.avatar} sizeClass="w-20 h-20 rounded-full object-cover" />
                    </div>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-white" />
                  </div>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">{profile?.name}</h4>
                <p className="text-sm font-semibold text-blue-600 mb-2">{profile?.position || 'หัวหน้า'}</p>
                <p className="text-xs text-gray-600 mb-4 leading-relaxed">{profile?.department}</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Phone" size={12} />
                    <span>{profile?.phone || '053-795-345'}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Mail" size={12} />
                    <span className="truncate">{profile?.email || 'info@thoeng.go.th'}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <Icon name="Users" size={14} />
                    <span>{computeStaffCount(dept?.id)} คน</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Level 3: หัวหน้ากลุ่มงาน */}
      <div className="flex justify-center">
        <div className="flex flex-row gap-4 lg:gap-6 flex-nowrap overflow-x-auto px-2 lg:px-0">
        {hierarchyLevels[2]?.departments?.map((dept) => {
           // Strict staff lookup to avoid wrong image
           const dbStaff = findStaffForDepartment(dept);
          
           console.log('Department:', dept?.name, 'Head:', dept?.head, 'Found staff:', dbStaff);
           console.log('dbStaff name:', dbStaff?.name, 'dept head:', dept?.head);
           console.log('dbStaff image_url:', dbStaff?.image_url);
          
          const baseProfile = staffProfiles[dept?.id] || (dept?.id === 8 ? staffProfiles['dept-8'] : {}) || {};
          const profile = {
            ...baseProfile,
            // ใช้ข้อมูลจาก departments ก่อน (ชื่อที่แก้ไขล่าสุด) ถ้าไม่มีค่อยใช้จาก org_structure
            name: dept?.head || dbStaff?.name || baseProfile?.name,
            position: dept?.headPosition || dbStaff?.position || baseProfile?.position,
            department: dept?.name || baseProfile?.department,
             avatar: getValidImageUrl(dbStaff?.image_url, baseProfile?.avatar),
            phone: dbStaff?.phone || baseProfile?.phone,
            email: dbStaff?.email || baseProfile?.email,
          };
          
          console.log('Final profile:', profile);
           console.log('Final profile avatar:', profile.avatar, 'from dbStaff image_url:', dbStaff?.image_url);
          // แสดงทุกกลุ่ม (ไม่ข้าม)
          if (profile?.subordinatesByLevel) {
            return (
              <div key={dept?.id}>{renderSubTreeProfile(profile)}</div>
            );
          }
          return (
            <div key={dept?.id} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 w-72 shrink-0">
              <div className="text-center">
                <div className="relative mx-auto mb-4">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg">
                     {profile?.avatar && profile.avatar !== '/assets/images/no_image.png' ? (
                       <img 
                         src={profile.avatar} 
                         alt={profile.name} 
                         className="w-20 h-20 rounded-full object-cover"
                         onClick={() => setPreview({ open: true, src: profile.avatar, alt: profile.name })}
                         style={{ cursor: 'zoom-in' }}
                         onError={(e) => {
                           e.target.style.display = 'none';
                           e.target.nextSibling.style.display = 'flex';
                         }}
                       />
                     ) : null}
                     <div 
                       className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xl font-bold"
                       style={{ display: profile?.avatar && profile.avatar !== '/assets/images/no_image.png' ? 'none' : 'flex' }}
                     >
                        {profile?.name?.charAt(0) || '?'}
                      </div>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-white" />
                  </div>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">{profile?.name}</h4>
                <p className="text-sm font-semibold text-blue-600 mb-2">{profile?.position || 'หัวหน้า'}</p>
                <p className="text-xs text-gray-600 mb-4 leading-relaxed">{profile?.department}</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Phone" size={12} />
                    <span>{profile?.phone || '053-795-345'}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Mail" size={12} />
                    <span className="truncate">{profile?.email || 'info@thoeng.go.th'}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <Icon name="Users" size={14} />
                    <span>{computeStaffCount(dept?.id)} คน</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* View Toggle */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          โครงสร้างองค์กร
        </h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('chart')}
            className={`
              px-3 py-2 rounded-md text-sm font-medium transition-government
              ${viewMode === 'chart' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }
            `}
          >
            <Icon name="GitBranch" size={16} className="mr-2" />
            แผนภูมิ
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`
              px-3 py-2 rounded-md text-sm font-medium transition-government
              ${viewMode === 'list' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }
            `}
          >
            <Icon name="List" size={16} className="mr-2" />
            รายการ
          </button>
          <button
            onClick={() => setViewMode('profile')}
            className={`
              px-3 py-2 rounded-md text-sm font-medium transition-government
              ${viewMode === 'profile' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }
            `}
          >
            <Icon name="Users" size={16} className="mr-2" />
            ข้อมูลบุคลากร
          </button>
        </div>
      </div>

      {/* Chart Content */}
      <div className="min-h-96">
        {viewMode === 'chart' ? renderChartView() :
         viewMode === 'list' ? renderListView() :
         renderProfileView()}
      </div>
      {preview.open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70" onClick={() => setPreview({ open: false, src: '', alt: '' })}>
          <div className="max-w-[90vw] max-h-[85vh] p-2 bg-transparent" role="dialog" aria-label={preview.alt}>
            <img src={preview.src} alt={preview.alt} className="max-w-full max-h-[80vh] rounded-lg shadow-2xl" />
            <div className="mt-2 text-center text-white text-sm truncate px-2">{preview.alt}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationChart;